import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { StoreContext } from "shared/context/store-context";
import { AuthContext } from "shared/context/auth-context";
import AutomationBase from "./AutomationBase";
import { SERVICE_TYPES, PLANS } from "@routebot/constants";
import { useRouteLimit } from "shared/hooks/useRouteLimit";
import RouteLimitBlocker from "components/shared/RouteLimitBlocker";

const CreatePersonelRoutesAutomation = ({ onClose, isShiftService = false }) => {
  const { t } = useTranslation();
  const { personelSchoolOptions, users } = useContext(StoreContext);
  const auth = useContext(AuthContext);

  // Route limit check
  const { limitStatus, isLimitReached, isUnlimited } = useRouteLimit();
  const shouldBlock = isLimitReached && !isUnlimited && auth?.company?.plan !== PLANS.UNLIMITED;
  // eslint-disable-next-line no-unused-vars
  const [processResults, setProcessResults] = useState(null);

  const automationConfig = {
    steps: [
      t("createPersonelRoutesAutomation.step1"),
      t("createPersonelRoutesAutomation.step2"),
      t("createPersonelRoutesAutomation.step3"),
      t("createPersonelRoutesAutomation.step4"),
    ],
    schoolOptions: personelSchoolOptions,
    passengers: users,
    serviceType: isShiftService ? SERVICE_TYPES.SHIFT : SERVICE_TYPES.PERSONEL,
    fileUploadConfig: {
      isFileUploadAllowed: true,
      exampleFileName: "passenger_list.xlsx",
      accept: ".xlsx",
    },
    texts: {
      schoolOptionsLabel: t("createPersonelRoutesAutomation.schoolOptionsLabel"),
      routeTypeLabel: t("createPersonelRoutesAutomation.routeTypeLabel"),
      maxNumberOfPassengersInARouteLabel: t("createPersonelRoutesAutomation.maxNumberOfPassengersInARouteLabel"),
      finishButtonText: t("createPersonelRoutesAutomation.finishButtonText"),
      maxNumberOfPassengersInARouteErrorText: t(
        "createPersonelRoutesAutomation.maxNumberOfPassengersInARouteErrorText",
      ),
      shiftOptionsLabel: t("createPersonelRoutesAutomation.shiftOptionsLabel"),
      passsengerSelection: {
        title: t("createPersonelRoutesAutomation.personelSelectionTitle"),
        subtitle: t("createPersonelRoutesAutomation.personelSelectionSubtitle"),
        selectPassengerLabel: t("createPersonelRoutesAutomation.selectPersonel"),
        addPassengerButtonText: t("createPersonelRoutesAutomation.addPersonel"),
        completeButtonText: t("createPersonelRoutesAutomation.complete"),
        emptyStateText: t("createPersonelRoutesAutomation.emptyStateText"),
        passengerLabel: t("createPersonelRoutesAutomation.personel"),
        selectedPassengersTitle: t("createPersonelRoutesAutomation.distributablePassengersTitle"),
        // Cards
        maxPassengersCardTitle: t("createPersonelRoutesAutomation.maxPassengersCardTitle"),
        routeTypeCardTitle: t("createPersonelRoutesAutomation.routeTypeCardTitle"),
        selectedPassengersCountTitle: t("createPersonelRoutesAutomation.selectedPassengersCountTitle"),
        // Sections
        notFoundHeader: t("createPersonelRoutesAutomation.notFoundHeaderBase"),
        notFoundDescription: t("createPersonelRoutesAutomation.notFoundDescription"),
        multipleHeader: t("createPersonelRoutesAutomation.multipleHeaderBase"),
        multipleDescription: t("createPersonelRoutesAutomation.multipleDescription"),
        noLocationHeader: t("createPersonelRoutesAutomation.noLocationHeaderBase"),
        noLocationDescription: t("createPersonelRoutesAutomation.noLocationDescription"),
        // Modal
        addAllModalTitle: t("createPersonelRoutesAutomation.addAllModalTitle"),
        modalCancelText: t("createPersonelRoutesAutomation.cancelButton"),
        modalAddText: t("createPersonelRoutesAutomation.addButton"),
        // Formatters
        formatAddAllButtonText: (count) => t("createPersonelRoutesAutomation.addAllButton", { count }),
        formatAddAllModalText: (count) => t("createPersonelRoutesAutomation.addAllModalText", { count }),
        // Toasts
        toastNoLocation: t("createPersonelRoutesAutomation.toastNoLocation"),
        toastAdded: t("createPersonelRoutesAutomation.toastAdded"),
        toastAlreadySelected: t("createPersonelRoutesAutomation.toastAlreadySelected"),
      },
    },
    onProcessComplete: async (data) => {
      setProcessResults(data);
      if (onClose) onClose();
    },
  };

  return (
    <React.Fragment>
      {shouldBlock && <RouteLimitBlocker limitStatus={limitStatus} onClose={onClose} isOverlay={true} />}
      <div className={shouldBlock ? "route-limit-blocked-form" : ""}>
        <AutomationBase {...automationConfig} />
      </div>
    </React.Fragment>
  );
};

export default CreatePersonelRoutesAutomation;
